import { type ChangeEvent, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getErrorMessage,
  useUpdateQuoteItem,
  type FieldSuggestion,
} from '@frontend/api';
import {
  Tooltip,
  Flex,
  FormControl,
  FormLabel,
  Checkbox,
  Skeleton,
  Text,
  useToast,
} from '@frontend/components';

import { quotePartViewer } from 'modules/quotePartViewer/data/actions';
import {
  getActiveComponentId,
  getIsQuoteItemLocked,
  getQuoteItemId,
  getQuoteNum,
  getQuoteRevision,
} from 'modules/quotePartViewer/data/reducer';
import type { PartDescriptorKey } from 'modules/quotePartViewer/types';
import {
  broadcastExportControlledChange,
  broadcastPartDescriptorChange,
  PartDescriptorChangePayload,
  usePropagateExportControlledStatusToViewerPage,
} from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { DisabledPartDescriptorTooltipLabel } from 'modules/quotes/quoteItems/components/util';
import { PartDescriptorWithSuggestionsFromPart } from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/PartDescriptorWithSuggestions';
import {
  getActivePart,
  getIsExportControlled,
  getIsLoading,
} from 'modules/viewerPage/data/reducer';

import { getIsSupplierDdtcRegistered } from 'store/session/selectors';

type PartDescriptorFormSkeletonProps = {
  isSupplierDdtcRegistered?: boolean;
};
const PartDescriptorFormSkeleton = ({
  isSupplierDdtcRegistered,
}: PartDescriptorFormSkeletonProps) => {
  return (
    <Flex direction="column" gap={1.5} w="100%">
      <Flex gap={1.5}>
        <FormControl
          flexGrow={10}
          flexShrink={1}
          flexBasis="192px"
          flexDirection="column"
        >
          <FormLabel>Part Number</FormLabel>
          <Skeleton h={4} />
        </FormControl>
        <FormControl
          flexGrow={1}
          flexShrink={0}
          flexBasis="96px"
          flexDirection="column"
        >
          <FormLabel>Revision</FormLabel>
          <Skeleton h={4} />
        </FormControl>
      </Flex>
      <FormControl flex="1 1 100%">
        <FormLabel>Description</FormLabel>
        <Skeleton h={4} />
      </FormControl>
      {isSupplierDdtcRegistered && (
        <Flex h={3} flex="1 1 55%" justify="flex-start" align="center">
          <Skeleton h={2} w={2} mr={1} />
          <Text>Export-controlled (ITAR)</Text>
        </Flex>
      )}
    </Flex>
  );
};

export const usePartDescriptorSyncSideEffects = () => {
  const dispatch = useDispatch();

  const activePart = useSelector(getActivePart);
  const { uuid, isRootPart } = activePart ?? {};

  const quoteItemId = useSelector(getQuoteItemId);
  const quoteNum = useSelector(getQuoteNum);
  const quoteRevision = useSelector(getQuoteRevision);
  const componentId = useSelector(getActiveComponentId);

  const handleBroadcastPartDescriptorSync = useCallback(
    (payload: PartDescriptorChangePayload) => {
      if (!componentId || !quoteItemId || !quoteNum || !uuid) {
        return;
      }

      const context = {
        isRootComponent: isRootPart ?? true,
        partUuid: uuid,
        componentId,
        quoteItemId,
        quoteNum,
        quoteRevision: quoteRevision ?? null,
      };
      broadcastPartDescriptorChange({
        context,
        payload,
      });
    },
    [uuid, componentId, quoteNum, quoteRevision, quoteItemId, isRootPart],
  );

  const additionalOnSave = useCallback(
    (name: PartDescriptorKey, newPartDescriptorValue: string | null) => {
      // Only update part descriptors on quote item if active part is root part
      if (
        isRootPart &&
        (name === 'partNumber' || name === 'revision' || name === 'description')
      ) {
        //Update partNumber or revision in quoteItem that part belongs to (so we see update in <QuoteItemHeader>)
        //Note: 'description' is not on the quote item
        dispatch(
          quotePartViewer.receiveQuoteItemUpdate({
            quoteItem: {
              id: quoteItemId,
              [name]: newPartDescriptorValue,
            },
          }),
        );
      }

      handleBroadcastPartDescriptorSync({ [name]: newPartDescriptorValue });
    },
    [dispatch, handleBroadcastPartDescriptorSync, isRootPart, quoteItemId],
  );

  return {
    additionalOnSave,
  };
};

export type PartDescriptorFormProps = {
  firstInputRef?: React.RefObject<HTMLDivElement>;
  fieldSuggestions: {
    partNumber: FieldSuggestion[];
    revision: FieldSuggestion[];
    description: FieldSuggestion[];
  };
};

export const PartDescriptorForm = ({
  firstInputRef,
  fieldSuggestions,
}: PartDescriptorFormProps) => {
  const isLoading = useSelector(getIsLoading);

  const activePart = useSelector(getActivePart);

  const { isRootPart } = activePart ?? {};

  const isPartExportControlled: boolean = useSelector(getIsExportControlled); //from the technical data package (not quote)
  const [isExportControlledChecked, setIsExportControlledChecked] = useState<
    boolean | undefined
  >(isPartExportControlled);

  const isSupplierDdtcRegistered = useSelector(getIsSupplierDdtcRegistered);

  const isQuoteItemLocked = useSelector(getIsQuoteItemLocked);

  const quoteItemId = useSelector(getQuoteItemId);

  useEffect(() => {
    if (isPartExportControlled !== isExportControlledChecked) {
      setIsExportControlledChecked(isPartExportControlled);
    }
  }, [isPartExportControlled, isExportControlledChecked]);

  const { additionalOnSave } = usePartDescriptorSyncSideEffects();

  const toast = useToast();

  const propagateControlledStatusToViewerPage =
    usePropagateExportControlledStatusToViewerPage();

  const { mutate: updateQuoteItem } = useUpdateQuoteItem({
    queryOptions: {
      onSuccess: (data) => {
        propagateControlledStatusToViewerPage({
          quoteItemId: data.id,
          isExportControlled: data.exportControlled,
        });

        broadcastExportControlledChange({
          context: {
            quoteItemId: data.id,
          },
          payload: {
            isExportControlled: data.exportControlled,
          },
        });
      },
      onError: (error) => {
        toast({
          title: 'Failed to update export controlled status',
          status: 'error',
          description: getErrorMessage(error),
        });
      },
    },
  });

  const handleExportControlledChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const isExportControlledChecked = (e.target as { checked: boolean })
        .checked;
      setIsExportControlledChecked(isExportControlledChecked);

      if (!quoteItemId) {
        return;
      }

      updateQuoteItem({
        quoteItemId,
        exportControlled: isExportControlledChecked,
      });
    },
    [quoteItemId, updateQuoteItem],
  );

  if (isLoading) {
    return (
      <PartDescriptorFormSkeleton
        isSupplierDdtcRegistered={isSupplierDdtcRegistered}
      />
    );
  }

  if (!quoteItemId || !activePart) {
    return <></>;
  }

  return (
    <Flex direction="column" gap={1.5}>
      <Flex gap={1.5}>
        <PartDescriptorWithSuggestionsFromPart
          name="partNumber"
          quoteItemId={quoteItemId}
          activePart={activePart}
          passedRef={firstInputRef}
          additionalOnSave={additionalOnSave}
          suggestions={fieldSuggestions.partNumber}
          display="flex"
          flexGrow={10}
          flexShrink={1}
          flexBasis="192px"
          flexDirection="column"
        />
        <PartDescriptorWithSuggestionsFromPart
          name="revision"
          quoteItemId={quoteItemId}
          activePart={activePart}
          additionalOnSave={additionalOnSave}
          suggestions={fieldSuggestions.revision}
          display="flex"
          flexGrow={1}
          flexShrink={0}
          flexBasis="96px"
          flexDirection="column"
        />
      </Flex>
      <PartDescriptorWithSuggestionsFromPart
        name="description"
        quoteItemId={quoteItemId}
        suggestions={fieldSuggestions.description}
        inputType="textarea"
        activePart={activePart}
        additionalOnSave={additionalOnSave}
        flex="1 1 100%"
      />

      {isSupplierDdtcRegistered && isRootPart && (
        <Tooltip
          isDisabled={!isQuoteItemLocked}
          label={
            <DisabledPartDescriptorTooltipLabel
              action="edit the ITAR status"
              isAnotherPersonEditing={isQuoteItemLocked}
            />
          }
          placement="right"
          shouldWrapChildren={isQuoteItemLocked}
        >
          <FormControl
            data-part-setup-field-export-controlled //For fullstory and chameleon
            id="isExportControlled"
          >
            <Checkbox
              disabled={isQuoteItemLocked}
              isChecked={isExportControlledChecked}
              isDisabled={isQuoteItemLocked}
              onChange={handleExportControlledChange}
            >
              Export-controlled (ITAR)
            </Checkbox>
          </FormControl>
        </Tooltip>
      )}
    </Flex>
  );
};

export default PartDescriptorForm;
